import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, AlertTriangle, TrendingDown, Users, Lightbulb, Activity, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const RISK = { High: "bg-red-100 text-red-700", Medium: "bg-amber-100 text-amber-700" };
const LEVEL = { Overloaded: "bg-red-100 text-red-700", High: "bg-amber-100 text-amber-700", Normal: "bg-emerald-100 text-emerald-700" };

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
    {(items || []).map((x, i) => <li key={i}>{typeof x === "string" ? x : (x.text || x.title || JSON.stringify(x))}</li>)}
  </ul>
);

function Insights() {
  const [d, setD] = useState(null);
  const load = () => { setD(null); api.get("/ai/intelligence").then((r) => setD(r.data)); };
  useEffect(() => { load(); }, []);
  if (!d) return <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={18} />Analysing…</div>;
  const Card = ({ title, icon: Icon, color, children }) => (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3 font-semibold text-slate-800"><Icon size={17} className={color} />{title}</div>
      {children}
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Created this wk", d.trends.created.this, d.trends.created.last],
          ["Closed this wk", d.trends.closed.this, d.trends.closed.last],
          ["Overdue now", d.trends.overdue_now, null],
          ["Bottleneck depts", d.bottlenecks.length, null]].map(([l, v, prev], i) => (
          <div key={i} className="bg-white border rounded-xl p-4" data-testid={`intel-stat-${i}`}>
            <div className="text-xs text-slate-500">{l}</div>
            <div className="text-2xl font-bold text-slate-900">{v}</div>
            {prev !== null && <div className="text-[11px] text-slate-400">last week {prev}</div>}
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Department Bottlenecks" icon={AlertTriangle} color="text-orange-500">
          <div className="space-y-2">
            {d.bottlenecks.map((b, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b last:border-0 pb-2" data-testid={`bottleneck-${i}`}>
                <div><div className="font-medium">{b.department}</div>
                  <div className="text-xs text-slate-500">{b.overdue} overdue · avg aging {b.avg_aging}d · blocks {b.downstream_impact}</div></div>
                <Badge variant="secondary">score {b.score}</Badge>
              </div>
            ))}
            {d.bottlenecks.length === 0 && <div className="text-sm text-slate-400">No significant bottlenecks.</div>}
          </div>
        </Card>
        <Card title="Predicted Delays" icon={TrendingDown} color="text-red-500">
          <div className="space-y-2 max-h-72 overflow-auto">
            {d.predicted_delays.map((t, i) => (
              <div key={i} className="text-sm border-b last:border-0 pb-2" data-testid={`delay-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-slate-400">{t.human_id}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded ${RISK[t.risk]}`}>{t.risk} risk</span>
                </div>
                <div className="font-medium truncate">{t.title}</div>
                <div className="text-xs text-slate-500">{(t.reasons || []).join(" · ")}</div>
              </div>
            ))}
            {d.predicted_delays.length === 0 && <div className="text-sm text-slate-400">No high-risk tasks detected.</div>}
          </div>
        </Card>
        <Card title="Predicted Workload (next 7 days)" icon={Users} color="text-blue-500">
          <div className="space-y-2 max-h-72 overflow-auto">
            {d.workload.map((w, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b last:border-0 pb-2" data-testid={`workload-${i}`}>
                <div><div className="font-medium">{w.name}</div>
                  <div className="text-xs text-slate-500">{w.due_this_week} due · {w.active} active</div></div>
                <span className={`text-[11px] px-2 py-0.5 rounded ${LEVEL[w.level]}`}>{w.projected_pct}% · {w.level}</span>
              </div>
            ))}
            {d.workload.length === 0 && <div className="text-sm text-slate-400">Capacity looks balanced.</div>}
          </div>
        </Card>
        <Card title="Recommendations" icon={Lightbulb} color="text-amber-500">
          <div className="space-y-2">
            {d.recommendations.map((r, i) => (
              <div key={i} className="text-sm border-b last:border-0 pb-2" data-testid={`rec-${i}`}>
                <div className="font-medium">{r.title}</div>
                <div className="text-xs text-slate-500">{r.reason}</div>
                <div className="text-[11px] text-emerald-600 mt-0.5">→ {r.impact}</div>
              </div>
            ))}
            {d.recommendations.length === 0 && <div className="text-sm text-slate-400">No recommendations right now.</div>}
          </div>
        </Card>
      </div>
      <div><Button variant="outline" size="sm" onClick={load} data-testid="intel-refresh"><RefreshCw size={14} className="mr-1" />Refresh</Button></div>
    </div>
  );
}

function Briefings() {
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState({});
  const run = async (kind, url) => {
    setBusy(kind);
    try { const r = await api.post(url); setRes((s) => ({ ...s, [kind]: r.data })); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI unavailable"); }
    setBusy("");
  };
  const Block = ({ kind, title, url, render }) => (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-slate-800">{title}</div>
        <Button size="sm" onClick={() => run(kind, url)} disabled={busy === kind} data-testid={`brief-${kind}`}>
          {busy === kind ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}<span className="ml-1">Generate</span>
        </Button>
      </div>
      {res[kind] ? render(res[kind]) : <div className="text-sm text-slate-400">Click generate for an AI briefing.</div>}
    </div>
  );
  return (
    <div className="space-y-4">
      <Block kind="daily" title="Daily Brief" url="/ai/daily-brief" render={(d) => {
        const a = d.ai;
        return a ? (<div className="space-y-2 text-sm"><div className="font-medium">{a.greeting}</div>
          <div><div className="text-xs font-semibold text-slate-500 uppercase">Focus today</div><List items={a.focus_today} /></div>
          <div><div className="text-xs font-semibold text-slate-500 uppercase">Watch-outs</div><List items={a.watchouts} /></div>
          <div className="text-slate-600 italic">{a.summary}</div></div>)
          : <div className="text-sm text-slate-500">{d.reason || "AI not available — showing raw counts."} </div>;
      }} />
      <Block kind="exec" title="Executive Brief" url="/ai/executive-summary" render={(d) => {
        const a = d.ai; const m = d.metrics;
        return (<div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">{Object.entries(m).map(([k, v]) => <Badge key={k} variant="secondary">{k.replace(/_/g, " ")}: {v}</Badge>)}</div>
          {a ? <>
            <div className="text-slate-800 font-medium">{a.headline}</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Top priorities</div><List items={a.top_priorities} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Key delays</div><List items={a.key_delays} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Bottlenecks</div><List items={a.department_bottlenecks} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Decisions required</div><List items={a.decisions_required} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Next 7 days</div><List items={a.next_7_days} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Risks</div><List items={a.risks} /></div>
            </div></> : <div className="text-slate-500">{d.reason}</div>}
        </div>);
      }} />
      <Block kind="weekly" title="Weekly Review" url="/ai/weekly-review" render={(d) => {
        const a = d.ai; const m = d.metrics;
        return (<div className="space-y-2 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">created {m.this_week.created} vs {m.last_week.created}</Badge>
            <Badge variant="secondary">closed {m.this_week.closed} vs {m.last_week.closed}</Badge>
            <Badge variant="secondary">overdue {m.overdue}</Badge>
          </div>
          {a ? <><div className="font-medium">{a.verdict}</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><div className="text-xs font-semibold text-slate-500 uppercase">What changed</div><List items={a.what_changed} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Why</div><List items={a.why_it_changed} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Attention required</div><List items={a.where_attention_required} /></div>
              <div><div className="text-xs font-semibold text-slate-500 uppercase">Next week</div><List items={a.next_week} /></div>
            </div></> : <div className="text-slate-500">{d.reason}</div>}
        </div>);
      }} />
    </div>
  );
}

function Summaries() {
  const { projects, departments } = useData();
  const [pid, setPid] = useState(""); const [did, setDid] = useState("");
  const [busy, setBusy] = useState(""); const [out, setOut] = useState({});
  const gen = async (kind, id) => {
    if (!id) return; setBusy(kind);
    try { const r = await api.post(`/ai/${kind}-summary/${id}`); setOut((s) => ({ ...s, [kind]: r.data })); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI unavailable"); }
    setBusy("");
  };
  const Render = ({ data }) => data?.ai ? (
    <div className="space-y-2 text-sm mt-2">
      {Object.entries(data.ai).map(([k, v]) => (
        <div key={k}><div className="text-xs font-semibold text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
          {Array.isArray(v) ? <List items={v} /> : <div className="text-slate-700">{String(v)}</div>}</div>
      ))}
    </div>
  ) : <div className="text-sm text-slate-500 mt-2">{data?.reason}</div>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white border rounded-xl p-4">
        <div className="font-semibold mb-2">Project Summary</div>
        <div className="flex gap-2">
          <Select value={pid} onValueChange={setPid}><SelectTrigger className="flex-1" data-testid="summary-project"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" onClick={() => gen("project", pid)} disabled={busy === "project" || !pid}>{busy === "project" ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}</Button>
        </div>
        {out.project && <Render data={out.project} />}
      </div>
      <div className="bg-white border rounded-xl p-4">
        <div className="font-semibold mb-2">Department Summary</div>
        <div className="flex gap-2">
          <Select value={did} onValueChange={setDid}><SelectTrigger className="flex-1" data-testid="summary-department"><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>{departments.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" onClick={() => gen("department", did)} disabled={busy === "department" || !did}>{busy === "department" ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}</Button>
        </div>
        {out.department && <Render data={out.department} />}
      </div>
    </div>
  );
}

function AISettings() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState(null);
  useEffect(() => { api.get("/ai/config").then((r) => setCfg(r.data)); api.get("/ai/requests").then((r) => setLog(r.data)); }, []);
  const save = async (next) => { setCfg(next); try { await api.put("/ai/config", next); toast.success("AI settings saved"); } catch { toast.error("Save failed"); } };
  if (!cfg) return <Loader2 className="animate-spin" size={18} />;
  const roles = ["master_admin", "admin", "manager", "employee", "viewer"];
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white border rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between"><div className="font-semibold">AI Enabled</div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ ...cfg, enabled: v })} data-testid="ai-enabled-switch" /></div>
        <div><div className="font-semibold mb-1 text-sm">Auto-apply record changes</div>
          <div className="flex items-center gap-2"><Switch checked={cfg.auto_apply_changes} onCheckedChange={(v) => save({ ...cfg, auto_apply_changes: v })} data-testid="ai-autoapply-switch" />
            <span className="text-xs text-slate-500">Let automations change records without confirmation</span></div></div>
        <div><div className="font-semibold mb-1 text-sm">Allowed roles</div>
          <div className="flex flex-wrap gap-2">{roles.map((r) => {
            const on = cfg.allowed_roles.includes(r);
            return <button key={r} data-testid={`ai-role-${r}`} onClick={() => save({ ...cfg, allowed_roles: on ? cfg.allowed_roles.filter((x) => x !== r) : [...cfg.allowed_roles, r] })}
              className={`text-xs px-2 py-1 rounded border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500"}`}>{r.replace("_", " ")}</button>;
          })}</div></div>
        <div><div className="font-semibold mb-1 text-sm">Modules</div>
          <div className="space-y-1">{Object.entries(cfg.modules).map(([m, v]) => (
            <div key={m} className="flex items-center justify-between text-sm"><span className="capitalize">{m.replace(/_/g, " ")}</span>
              <Switch checked={v} onCheckedChange={(nv) => save({ ...cfg, modules: { ...cfg.modules, [m]: nv } })} data-testid={`ai-module-${m}`} /></div>
          ))}</div></div>
        <div className="text-xs text-slate-500">Main model: <b>{cfg.main_model.model}</b> · Light: <b>{cfg.light_model.model}</b></div>
      </div>
      <div className="bg-white border rounded-xl p-4">
        <div className="font-semibold mb-2">AI Usage Log</div>
        {log ? <>
          <div className="flex gap-2 mb-2"><Badge variant="secondary">total {log.total}</Badge><Badge variant="secondary">success {log.success}</Badge></div>
          <div className="text-xs text-slate-500 mb-2">{Object.entries(log.by_feature).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}</div>
          <div className="max-h-80 overflow-auto text-xs divide-y">
            {log.items.map((r) => (
              <div key={r.id} className="py-1.5 flex items-center justify-between" data-testid="ai-log-row">
                <span>{r.feature} <span className="text-slate-400">· {r.model}</span></span>
                <span className={r.ok ? "text-emerald-600" : "text-red-600"}>{r.ok ? `${r.duration_ms}ms` : "failed"}</span>
              </div>
            ))}
          </div></> : <Loader2 className="animate-spin" size={16} />}
      </div>
    </div>
  );
}

export default function Intelligence() {
  const { user } = useAuth();
  const isAdmin = ["master_admin", "admin"].includes(user?.role);
  return (
    <div className="p-6">
      <PageHeader title="Intelligence Center" subtitle="AI-assisted insights, briefings and summaries — every conclusion traces back to your data" />
      <Tabs defaultValue="insights">
        <TabsList className="mb-4">
          <TabsTrigger value="insights" data-testid="tab-insights"><Activity size={15} className="mr-1" />Insights</TabsTrigger>
          <TabsTrigger value="briefings" data-testid="tab-briefings"><Sparkles size={15} className="mr-1" />Briefings</TabsTrigger>
          <TabsTrigger value="summaries" data-testid="tab-summaries">Summaries</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="tab-ai-settings">AI Settings</TabsTrigger>}
        </TabsList>
        <TabsContent value="insights"><Insights /></TabsContent>
        <TabsContent value="briefings"><Briefings /></TabsContent>
        <TabsContent value="summaries"><Summaries /></TabsContent>
        {isAdmin && <TabsContent value="settings"><AISettings /></TabsContent>}
      </Tabs>
    </div>
  );
}
